import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { scenarioData, selectorCopy, defaultScenarioId } from "../config/media";

const EASE = [0.16, 1, 0.3, 1];

export const FamilySelector = () => {
  const [activeId, setActiveId] = useState(defaultScenarioId);
  const active = scenarioData.find((s) => s.id === activeId);

  return (
    <section
      data-testid="family-selector-section"
      className="px-6 lg:px-10 py-20 lg:py-28"
    >
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-120px" }}
        transition={{ duration: 0.8, ease: EASE }}
        className="max-w-[560px]"
      >
        <p
          data-testid="selector-eyebrow"
          className="text-xs font-semibold uppercase tracking-[0.22em] text-[var(--strollee-accent)]"
        >
          {selectorCopy.eyebrow}
        </p>
        <h2 className="mt-4 text-[36px] lg:text-[48px] font-semibold leading-[1.1] tracking-[-0.03em] text-[var(--strollee-ink)]">
          {selectorCopy.headlineLines.map((line) => (
            <span key={line} className="block">
              {line}
            </span>
          ))}
        </h2>
        <p className="mt-5 text-base lg:text-lg leading-relaxed text-[var(--strollee-muted)]">
          {selectorCopy.support}
        </p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.7, ease: EASE, delay: 0.1 }}
        className="mt-10 inline-flex items-center gap-1 rounded-full border border-[var(--strollee-line)] bg-[var(--strollee-surface)] p-1.5"
        role="tablist"
        aria-label="Family scenarios"
        data-testid="scenario-tablist"
      >
        {scenarioData.map((s) => {
          const isActive = s.id === activeId;
          return (
            <button
              key={s.id}
              role="tab"
              aria-selected={isActive}
              data-testid={`scenario-tab-${s.id}`}
              onClick={() => setActiveId(s.id)}
              className={`relative rounded-full px-5 py-2.5 text-[13px] transition-colors duration-200 ${
                isActive
                  ? "font-semibold text-[var(--strollee-surface)]"
                  : "font-medium text-[var(--strollee-muted)] hover:text-[var(--strollee-ink)]"
              }`}
            >
              {isActive && (
                <motion.span
                  layoutId="scenario-active-pill"
                  transition={{ duration: 0.4, ease: EASE }}
                  className="absolute inset-0 rounded-full bg-[var(--strollee-ink)]"
                />
              )}
              <span className="relative z-10">{s.label}</span>
            </button>
          );
        })}
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8, ease: EASE, delay: 0.15 }}
        className="mt-12 grid grid-cols-12 items-center gap-8 lg:gap-14"
      >
        <div className="col-span-12 lg:col-span-8">
          <div className="relative h-[420px] lg:h-[600px] overflow-hidden rounded-[var(--strollee-radius-hero)] bg-[var(--strollee-line)]/40">
            {scenarioData.map((s) => (
              <motion.img
                key={s.id}
                src={s.image.url}
                alt={s.image.alt}
                data-testid={s.id === activeId ? "scenario-media-active" : `scenario-media-${s.id}`}
                initial={false}
                animate={{
                  opacity: s.id === activeId ? 1 : 0,
                  scale: s.id === activeId ? 1 : 1.03,
                }}
                transition={{ duration: 0.7, ease: EASE }}
                style={s.image.position ? { objectPosition: s.image.position } : undefined}
                className="absolute inset-0 h-full w-full object-cover"
              />
            ))}
            <AnimatePresence mode="wait">
              <motion.span
                key={active.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.35, ease: EASE }}
                data-testid="scenario-media-tag"
                className="absolute left-6 top-6 inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.22em] text-white backdrop-blur-md"
              >
                <span className="h-1.5 w-1.5 rounded-full bg-[#8FB89E]" />
                {active.label}
              </motion.span>
            </AnimatePresence>
          </div>
        </div>

        <div className="col-span-12 lg:col-span-4">
          <AnimatePresence mode="wait">
            <motion.div
              key={active.id}
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.45, ease: EASE }}
              className="max-w-[420px]"
            >
              <h3
                data-testid="scenario-title"
                className="text-[28px] lg:text-[34px] font-semibold leading-[1.15] tracking-[-0.02em] text-[var(--strollee-ink)]"
              >
                {active.title}
              </h3>
              <p
                data-testid="scenario-copy"
                className="mt-4 text-base leading-relaxed text-[var(--strollee-muted)]"
              >
                {active.copy}
              </p>
              <div className="mt-8">
                <a
                  href="#setup"
                  data-testid="scenario-explore-cta"
                  className="group inline-flex items-center gap-2 rounded-full border border-[var(--strollee-ink)]/25 px-6 py-3 text-sm font-semibold tracking-wide text-[var(--strollee-ink)] transition-all duration-200 hover:bg-[var(--strollee-ink)] hover:text-[var(--strollee-surface)] hover:-translate-y-[1px]"
                >
                  {selectorCopy.exploreCta}
                  <ArrowUpRight
                    size={16}
                    strokeWidth={2.2}
                    className="transition-transform duration-200 group-hover:translate-x-[2px] group-hover:-translate-y-[2px]"
                  />
                </a>
              </div>
              <p className="mt-7 text-sm text-[var(--strollee-muted)]">
                {selectorCopy.quizHint}{" "}
                <a
                  href="#find-my-setup"
                  data-testid="find-my-setup-link"
                  className="group inline-flex items-center gap-1 font-semibold text-[var(--strollee-accent)] transition-colors duration-200 hover:text-[var(--strollee-accent-deep)]"
                >
                  {selectorCopy.quizLink}
                  <ArrowRight
                    size={14}
                    strokeWidth={2.4}
                    className="transition-transform duration-200 group-hover:translate-x-[2px]"
                  />
                </a>
              </p>
            </motion.div>
          </AnimatePresence>
        </div>
      </motion.div>
    </section>
  );
};
